import type { EventBus } from "../core/event-bus.js";
import type { PhysicsStore, TrialRow } from "../core/store.js";
import type { ExperimentPack, StageDefinition, StepDefinition } from "../data/schemas.js";
import { submitChoice } from "../interaction/choice-controller.js";
import { performAction, resetActState, type ActionPayload, type PhysicsActionId } from "../interaction/action-controller.js";
import { showKnowledge } from "../knowledge/knowledge-engine.js";
import { checkVariableSelection } from "../logic/rule-engine.js";
import { checkActStep, checkRecordStep } from "./step-checker.js";
import { hintOnRequested, hintOnStepEntered, hintOnWrongAnswer, type HintDecision } from "./hint-engine.js";

export interface StepRecord {
  stepId: string;
  status: "pending" | "passed" | "blocked";
  attempts: number;
  correct?: boolean | null;
}

export interface StepTransition {
  stepId: string;
  status: StepRecord["status"];
  correct?: boolean | null;
  advanced: boolean;
  completed?: boolean;
  nextStepId?: string;
  nextStageId?: string;
}

/**
 * 任务引擎：guess→design→act→record→conclude 状态机（tech/108 §4.5）。
 *
 * 只有 act/record 是硬门槛，其余 kind 提交即推进——错误只触发知识卡和事件记录，从不阻塞学生
 * （对齐 000 文档 §七"允许孩子犯错"，是本模块与化学 mission-engine 最大的行为差异）。
 */
export class MissionEngine {
  private stageIndex = 0;
  private stepIndex = 0;
  private readonly records = new Map<string, StepRecord>();

  constructor(
    private readonly pack: ExperimentPack,
    private readonly store: PhysicsStore,
    private readonly bus: EventBus,
  ) {}

  get currentStage(): StageDefinition {
    return this.pack.mission.stages[this.stageIndex];
  }

  get currentStep(): StepDefinition {
    return this.currentStage.steps[this.stepIndex];
  }

  start(): void {
    this.stageIndex = 0;
    this.stepIndex = 0;
    this.records.clear();
    this.bus.emit("experiment.started", { experimentId: this.pack.experiment.id });
    this.enterStage();
    this.enterStep();
  }

  /** guess / design / pattern / conclude 四类单选题提交入口。 */
  submitChoice(optionId: string): StepTransition {
    const step = this.currentStep;
    if (
      step.kind !== "guess" &&
      step.kind !== "design" &&
      step.kind !== "pattern" &&
      step.kind !== "conclude"
    ) {
      throw new Error(`[mission-engine] step "${step.id}" 不是选择题（kind=${step.kind}）`);
    }
    const submission = submitChoice(step.options ?? [], optionId);
    const record = this.recordFor(step.id);
    record.attempts += 1;

    let correct = submission.correct;
    let eventType: string;
    if (step.kind === "guess") {
      eventType = "guess.submitted";
    } else if (step.kind === "design") {
      eventType = "variable.selected";
      if (submission.selected.variableSelection) {
        correct = checkVariableSelection(submission.selected.variableSelection, {
          independentVar: this.currentStage.independentVar,
          controlledVars: this.currentStage.controlledVars,
        }).correct;
      }
    } else if (step.kind === "pattern") {
      eventType = "pattern.selected";
    } else {
      eventType = step.conclusionKind === "pattern" ? "pattern.selected" : "conclusion.submitted";
    }

    record.correct = correct;
    record.status = "passed";
    this.bus.emit(eventType, { stepId: step.id, optionId, correct, attempt: record.attempts });

    if (correct === false) {
      this.bus.emit("error.triggered", { stepId: step.id, kind: step.kind });
      this.triggerHint(hintOnWrongAnswer(step));
    }

    return this.completeStep(step, record, correct);
  }

  /** act 步骤的具名操作入口。 */
  performAction(actionId: PhysicsActionId, payload?: ActionPayload): StepTransition {
    const step = this.currentStep;
    if (step.kind !== "act") {
      throw new Error(`[mission-engine] step "${step.id}" 不是操作步骤（kind=${step.kind}）`);
    }
    const result = performAction(this.store, actionId, payload);
    this.store.recordAction(step.id, actionId);
    this.bus.emit("equipment.selected", { stepId: step.id, actionId });
    for (const variableId of result.changedVariables) {
      this.bus.emit("adjustment.changed", {
        stepId: step.id,
        variableId,
        value: this.store.variables.get(variableId)?.value ?? null,
      });
    }
    if (actionId === "pull_uniform") {
      this.bus.emit("measurement.read", {
        stepId: step.id,
        normalForce: this.store.variables.get("normalForce")?.value ?? null,
        frictionReading: this.store.variables.get("frictionReading")?.value ?? null,
      });
    }

    const record = this.recordFor(step.id);
    record.attempts += 1;

    const gate = checkActStep(this.store, step);
    if (!gate.passed) {
      record.status = "blocked";
      this.bus.emit("step.blocked", { stepId: step.id });
      return { stepId: step.id, status: "blocked", advanced: false };
    }
    return this.completeStep(step, record, undefined);
  }

  /** record 步骤：把 recordFields 从当前变量表快照成数据表的一行。 */
  recordTrial(): StepTransition {
    const step = this.currentStep;
    if (step.kind !== "record") {
      throw new Error(`[mission-engine] step "${step.id}" 不是记录步骤（kind=${step.kind}）`);
    }
    const fields = step.recordFields ?? [];
    const row: TrialRow = {};
    for (const fieldId of fields) {
      row[fieldId] = this.store.variables.get(fieldId)?.value ?? null;
      this.store.markRecorded(fieldId);
    }
    const trialCount = this.store.appendTrial(this.currentStage.id, row);
    this.bus.emit("data.recorded", { stepId: step.id, stageId: this.currentStage.id, row, trialCount });

    const record = this.recordFor(step.id);
    record.attempts += 1;

    const gate = checkRecordStep(this.store, this.currentStage.id, step);
    if (!gate.passed) {
      record.status = "blocked";
      this.bus.emit("step.blocked", { stepId: step.id });
      return { stepId: step.id, status: "blocked", advanced: false };
    }
    return this.completeStep(step, record, undefined);
  }

  /** record 步骤未凑够组数时，允许回到本 stage 的 act 步骤改变条件重测——试次数据表不清空，
   * 回环完全在 stepIndex 层面（真机第六轮反馈：多试次阶段没有路径拿到真正不同的数据，"记录"
   * 变成原地重复快照同一个数值）。不强制条件必须真的改变，只打开这条路径。
   * 但 act 步骤的动作计数按 (stepId, actionId) 累加、从不自动清零（store.ts `recordAction`）——
   * 回到同一个 act stepId，门控会读到第一次通过时留下的旧计数，"免费"立刻满足，学生不需要真的
   * 重新拉一次测力计。必须清掉这个 step 的动作计数，才能强制门控在第二轮真正重新满足。
   * 同时用 resetActState 把该 act 步骤会改动的变量复位（砝码数/材质/朝向/测力计读数），
   * 让"操作台"视觉状态和已清空的门控保持一致——否则砝码还画在木块上、门控却不认（真机反馈
   * "返回操作台后卡在还差一步"的确凿根因）。 */
  returnToAct(): StepTransition {
    const step = this.currentStep;
    if (step.kind !== "record") {
      throw new Error(`[mission-engine] step "${step.id}" 不是记录步骤（kind=${step.kind}），无法返回操作步骤`);
    }
    const actIndex = this.currentStage.steps.findIndex((entry) => entry.kind === "act");
    if (actIndex === -1) {
      throw new Error(`[mission-engine] stage "${this.currentStage.id}" 没有 act 步骤`);
    }
    this.stepIndex = actIndex;
    const actStep = this.currentStep;
    this.store.clearActionsForStep(actStep.id);
    resetActState(this.store, actStep.requiredActions ?? []);
    this.enterStep();
    return { stepId: this.currentStep.id, status: "pending", advanced: true, nextStepId: this.currentStep.id, nextStageId: this.currentStage.id };
  }

  /**
   * 中考题作答。两种用法：
   * - `conclude` 步骤的附加中考题（`step.exam`）：不绑定 Step 推进，随时可作答（tech/108 §7）。
   * - `exam` 步骤（章节末综合中考）：作为独立一步，作答后走 completeStep/advance 正常推进。
   */
  submitExam(optionId: string): StepTransition {
    const step = this.currentStep;
    const exam = step.exam;
    if (!exam) {
      throw new Error(`[mission-engine] step "${step.id}" 无中考题`);
    }
    const submission = submitChoice(exam.options, optionId);
    this.bus.emit("exam.answer.submitted", {
      stepId: step.id,
      examId: exam.id,
      optionId,
      correct: submission.correct,
    });
    if (submission.correct === false) {
      this.bus.emit("error.triggered", { stepId: step.id, kind: "exam" });
    }

    if (step.kind === "exam") {
      const record = this.recordFor(step.id);
      record.attempts += 1;
      record.correct = submission.correct;
      return this.completeStep(step, record, submission.correct);
    }

    return { stepId: step.id, status: "passed", correct: submission.correct, advanced: false };
  }

  /** summary 步骤（章节末总结）：无对错、无门槛，点"继续"即推进——总结本身不是判断点。 */
  submitSummary(): StepTransition {
    const step = this.currentStep;
    if (step.kind !== "summary") {
      throw new Error(`[mission-engine] step "${step.id}" 不是总结步骤（kind=${step.kind}）`);
    }
    const record = this.recordFor(step.id);
    record.attempts += 1;
    record.status = "passed";
    return this.completeStep(step, record, null);
  }

  /** knowledge 步骤（⑥ 形成知识点）：无对错、无门槛，点"继续"即推进——知识展示本身不是判断点。
   * 知识卡在 enterStep() 时已通过 knowledgeRefs[0] 自动带出（见 hint-engine.ts），这里只负责推进。 */
  submitKnowledge(): StepTransition {
    const step = this.currentStep;
    if (step.kind !== "knowledge") {
      throw new Error(`[mission-engine] step "${step.id}" 不是知识点步骤（kind=${step.kind}）`);
    }
    const record = this.recordFor(step.id);
    record.attempts += 1;
    record.status = "passed";
    return this.completeStep(step, record, null);
  }

  requestHint(): void {
    const step = this.currentStep;
    this.bus.emit("hint.requested", { stepId: step.id });
    const hint = hintOnRequested(step);
    if (hint && this.triggerHint(hint)) {
      this.bus.emit("hint.viewed", { stepId: step.id, knowledgeId: hint.knowledgeId });
    }
  }

  private completeStep(step: StepDefinition, record: StepRecord, correct: boolean | null | undefined): StepTransition {
    record.status = "passed";
    this.bus.emit("step.completed", { stepId: step.id });
    return this.advance(step.id, correct);
  }

  private advance(stepId: string, correct: boolean | null | undefined): StepTransition {
    const stage = this.currentStage;
    const isLastStepInStage = this.stepIndex === stage.steps.length - 1;

    if (!isLastStepInStage) {
      this.stepIndex += 1;
      this.enterStep();
      return {
        stepId,
        status: "passed",
        correct,
        advanced: true,
        nextStepId: this.currentStep.id,
        nextStageId: stage.id,
      };
    }

    this.bus.emit("stage.completed", { stageId: stage.id });
    const isLastStage = this.stageIndex === this.pack.mission.stages.length - 1;
    if (isLastStage) {
      this.bus.emit("experiment.completed", { experimentId: this.pack.experiment.id });
      return { stepId, status: "passed", correct, advanced: true, completed: true };
    }

    this.stageIndex += 1;
    this.stepIndex = 0;
    this.enterStage();
    this.enterStep();
    return {
      stepId,
      status: "passed",
      correct,
      advanced: true,
      nextStepId: this.currentStep.id,
      nextStageId: this.currentStage.id,
    };
  }

  private enterStage(): void {
    this.bus.emit("stage.started", { stageId: this.currentStage.id });
  }

  private enterStep(): void {
    const step = this.currentStep;
    this.bus.emit("step.started", { stepId: step.id, kind: step.kind });
    this.triggerHint(hintOnStepEntered(step));
  }

  private triggerHint(hint: HintDecision | undefined): boolean {
    if (!hint) return false;
    const shown = showKnowledge(this.pack.knowledge, hint.knowledgeId);
    if (!shown) return false;
    this.bus.emit("knowledge.shown", { knowledgeId: hint.knowledgeId, reason: hint.reason });
    return true;
  }

  private recordFor(stepId: string): StepRecord {
    let record = this.records.get(stepId);
    if (!record) {
      record = { stepId, status: "pending", attempts: 0 };
      this.records.set(stepId, record);
    }
    return record;
  }
}
